import {API, Lib} from "habit-core";

export type StationMode =
    | "front_of_house_1"
    | "front_of_house_2"
    | "line_buster"
    | "drive_thru_order_taker"
    | "drive_thru_order_fulfillment";

export type Settings = {
    stationMode: StationMode;
    laneAssignment: LaneAssignment | null;
};

export type PrinterStatus = "none" | "offline" | "online";

export type Station = {
    mode: StationMode;
    name: string;
    printerStatus: PrinterStatus;
    backups: string[];
};

export type OrderStatus =
    | "in_creation"
    | "pending"
    | "paid"
    | "stored"
    | "cancelled"
    | "error";
export type OrderType =
    | "in_store"
    | "drive_thru"
    | "carry_out"
    | "curbside"
    | "pickup_lane"
    | "uber"
    | "grubhub"
    | "doordash"
    | "ez_cater_pickup"
    | "ez_cater_delivery";
export type OrderSource =
    | "web"
    | "mobile"
    | "delivery"
    | "kiosk"
    | "pos"
    | "phone"
    | "doordash"
    | "web_doordash"
    | "mobile_doordash"
    | "postmates"
    | "uber"
    | "grubhub"
    | "tictuk"
    | "ez_cater";
export type TransactionTender =
    | "cash"
    | "gift_card"
    | "credit"
    | "comp_card"
    | "ez_cater"
    | "external_tender";
export type TransactionCardType =
    | "Visa"
    | "Discover Card"
    | "Mastercard"
    | "AMEX"
    | "Gift Card"
    | "Card";
export type ManagerDiscountCode =
    | "customer100"
    | "certificateOfAchievement"
    | "employee100"
    | "employee20"
    | "student10"
    | "employee50";

export type DriveThruDetails = {
    vehicleType: string;
    vehicleColor: string;
    eatingInCar: boolean;
    laneNumber: 1 | 2;
};

export type OrderTransaction = {
    id: string;
    tender: TransactionTender;
    tenderName?: string | null;
    cardType?: TransactionCardType | null;
    amountCents: API.models.USDCents;
};

export type OrderGiftCard = {
    cardNumber: string;
    amountCents: API.models.USDCents;
};

export type OrderModifier = API.models.OrderItemModifier & {
    modifierName: string | null;
    modifierSelectionName: string | null;
    type: API.models.MenuItemModifierSelectionType | null;
};

// if the digital API starts sending names in the payload, we can edit those models directly.
export type OrderItem = Omit<API.models.OrderItem, "modifiers"> & {
    name: string;
} & {
    modifiers: OrderModifier[];
    dateAdded: string;
    dateModified: string;
};

export type OrderCombo = Omit<API.models.OrderCombo, "items"> & {
    name: string;
} & {
    items: (Omit<API.models.OrderComboItem, "modifiers"> & {
        name: string;
        modifiers: OrderModifier[];
    })[];
    dateAdded: string;
    dateModified: string;
};

export type OrderDiscount = API.models.OrderDiscount & {
    description?: string;
    redemptionCode?: string;
};

export type UnprocessedDiscount = {
    code: string;
    id: string;
    amountCents: API.models.USDCents;
    type: DiscountType;
};

export type Order = {
    id: string;
    checkNumber: string;
    orderNumber: number;
    referenceNumber: number;
    orderDate: string;
    pickupTime?: string | null;
    startTime: string;
    cancelledTime?: string | null;
    cancelledManagerName?: string | null;
    status: OrderStatus;
    error?: string | null;
    qsrError?: string | null;
    guestName?: string | null;
    guestPhoneLast4?: string | null;
    pagerNumber?: number | null;
    type: OrderType;
    driveThruDetails?: DriveThruDetails | null;
    source: OrderSource;
    stationMode?: StationMode | null;
    subtotalCents: API.models.USDCents;
    discountCents: API.models.USDCents;
    totalBeforeTaxCents: API.models.USDCents;
    serviceChargeCents: API.models.USDCents;
    taxCents: API.models.USDCents;
    totalCents: API.models.USDCents;
    storeId: string;
    combos: OrderCombo[];
    items: OrderItem[];
    giftCards: OrderGiftCard[];
    discounts: OrderDiscount[];
    unprocessedDiscounts?: UnprocessedDiscount[];
    serviceCharges: API.models.OrderServiceCharge[];
    transactions: OrderTransaction[];
    charityRoundUpSelected: boolean;
    // charityRoundUpAmountCents: number;
    charityAmountCents: number;
    entireOrderTaxExempt: boolean;
};

export type Modifier = {
    modifierId: string;
    modifierSelectionId: string;
    quantity: number;
};

export type ItemAddOnsInfo = {
    itemId: string;
    addOnItemIds: string[];
};

export type AllPrepModifierSelection = {
    id: string;
    name: string;
    priceCents: API.models.USDCents;
    min: number;
    max: number;
    type?: API.models.MenuItemModifierSelectionType | null;
};

export type AllPrepsModifier = {
    id: string;
    name: string;
    defaultModifierSelectionId: string;
    selections: AllPrepModifierSelection[];
    digitalOnly: boolean;
    isAllergy: boolean;
};

export type AllPrepsItemModifierData = Lib.selectors.ItemModifierData & {
    digitalOnly: boolean;
    isAllergy: boolean;
};

export type ItemCustomization = {
    itemId: string;
    modifiers: Modifier[];
};

export type OrderComboRequest = {
    comboId: string;
    quantity: number;
    items?: ItemCustomization[] | null;
    dateAdded: Date;
    dateModified: Date;
};

export type OrderItemRequest = {
    quantity: number;
    customization: ItemCustomization;
    dateAdded: Date;
    dateModified: Date;
};

export type OrderGiftCardRequest = {
    cardNumber: string;
    amountCents: API.models.USDCents;
};

export type RedemptionDetail = {
    code: string | null; // for punchh redemption code (i.e barcode value) or code for tracks and manager discounts
    id: string | null; // for punchh rewardId
    amountCents: number;
    type: DiscountType;
    employeeId: string | null;
    managerId: string | null;
    details: POSDiscountDetails | null;
};

export type OrderParsed = Omit<
    Order,
    "orderDate" | "pickupTime" | "startTime" | "cancelledTime"
> & {
    orderDate: Date;
    pickupTime: Date | null;
    startTime: Date;
    cancelledTime: Date | null;
};

export type PendingVoidedItem = {
    id: string;
    itemCustomizationId: string;
    quantity: number;
};

export type PendingVoidedCombo = {
    id: string;
    comboCustomizationId: string;
    quantity: number;
};

export type VoidedItem = PendingVoidedItem & {
    reason: string;
};

export type VoidedCombo = PendingVoidedCombo & {
    reason: string;
};

export type CharityMode =
    | "round_up_only"
    | "round_up_and_dollars"
    | "dollars_only"
    | "round_up_and_custom";

export type CharitySettings = {
    isEnabled: boolean;
    mode: CharityMode | null;
    charityName: string | null;
    prompt: string | null;
    rewardMinimumCents: API.models.USDCents | null;
    rewardMessage: string | null;
};

export type StoreSettings = {
    storeId: string;
    storeNumber: number;
    taxRate: number;
    taxExemptEnabled?: boolean;
    autoComboEnabled: boolean;
    charitySettings: CharitySettings;
    address: API.models.Address;
    isParallelMode: boolean;
    isLab: boolean;
    maxGiftCardCashOutCents: API.models.USDCents;
    externalTenders: ExternalTender[] | null;
    penniesUnavailable: boolean | null;
    kioskCashOrderPagerNumber: number | null;
    instoreReceiptMessage: string | null;
    digitalReceiptMessage: string | null;
};

export type ExternalTender = {
    name: string;
    isTaxExempt: boolean;
};

export type Manager = {
    firstName: string;
    lastName: string;
    id: string;
};

export type DiscountDetails = {
    name?: string;
    discountCode: string | null;
    rewardId: string | null;
    employeeId?: string;
    managerId?: string; // for manager discounts, we just want to report this
    reason?: string;
    amountCents: API.models.USDCents | null;
    amountPercentage: number | null;
    discountType: DiscountType;
    details?: POSDiscountDetails;
};

export type AppliedGiftCard = {
    id: string;
    appliedCents: API.models.USDCents;
    balanceRemainingCents: API.models.USDCents;
};

export type AppliedCompCard = {
    id: string;
    balance: API.models.USDCents;
    appliedCents?: API.models.USDCents;
};

export type AppliedExternalTender = {
    tenderName: string;
    isTaxExempt: boolean;
    appliedCents?: API.models.USDCents;
};

export type VonageCCType = "AMEX" | "VISA" | "MC" | "DISCOVER";

export type AppliedCreditCard = {
    cardNumber: string;
    appliedAmountCents: API.models.USDCents;
    authId: string;
    termId: string;
    merchId: string;
    ccType: VonageCCType;
    cardholderName: string | null;
    cardExpMonth: string;
    cardExpYear: string;
    cardToken: string;
    ctroutd: string;
    troutd: string;
    tipAmountCents: API.models.USDCents;
};

export type ItemDiscountInfo = {
    quantity: number;
    customization: {
        itemId: string;
        modifiers: {
            modifierId: string;
            modifierSelectionId: string;
            quantity: number;
        }[];
    };
};

export type ComboDiscountInfo = {
    comboId: string;
    quantity: number;
    items: {
        itemId: string;
        modifiers: {
            modifierId: string;
            modifierSelectionId: string;
            quantity: number;
        }[];
    }[];
};

export type DiscountType = "punchh" | "manager" | "tracks" | "ez_cater";

export type POSDiscountDetails = {
    name: string;
    description: string;
};

export type DiscountResponse = {
    status: string;
    message: string;
    redemptionAmountCents: API.models.USDCents | null;
    redemptionAmountPercentage: number | null;
    qualifiedItemIds: string[];
    qualifiedComboIds: string[];
    discountType: DiscountType;
    details: POSDiscountDetails | null;
};

export type GiftCardAddFundsCartEntity = {
    id: string;
    cardNumber: string;
    originalBalanceCents: API.models.USDCents;
    amountToAddCents: API.models.USDCents;
};

export type GiftCardPurchaseCartEntity = {
    id: string;
    cardNumbers?: string[];
    balanceCents: API.models.USDCents;
    quantity: number;
};

export type GiftCardsIncreaseBalanceResponse = {
    giftCards: {
        cardNumber: string;
        balanceCents: API.models.USDCents;
    }[];
};

export type GiftCardsReduceBalanceResponse = {
    balanceCents: API.models.USDCents;
    amountCents: API.models.USDCents;
    cardToken: string;
    authId: string;
    cardType: CardType;
};

export type CardType = "gift" | "comp";

export type PaymentType =
    | "credit"
    | "cash"
    | "gift_card"
    | "comp_card"
    | "ez_cater"
    | "external_tender";

export type PaymentDetail = {
    type: PaymentType;
    tenderName?: string | null;
    amountCents: API.models.USDCents;
    changeOwedCents?: API.models.USDCents;
    changeOwedRoundingAdjustmentCents?: API.models.USDCents | null;
    balanceCents?: API.models.USDCents;
    maskedCardNumber?: string;
    authId?: string;
    termId?: string;
    merchId?: string;
    ccType?: VonageCCType;
    cardholderName?: string | null;
    cardExpMonth?: string;
    cardExpYear?: string;
    cardToken?: string;
    ctroutd?: string;
    troutd?: string;
    tipCents?: API.models.USDCents;
};

export type TimePeriodReport = {
    allDay: TimeReportSection;
    lunch: TimeReportSection;
    dinner: TimeReportSection;
    halfHour: TimeReportSection[];
    openTime: string;
    closeTime: string;
};

export type TimeReportSection = {
    netSalesCents: API.models.USDCents;
    checkAverageCents: API.models.USDCents;
    numChecks: number;
    hereSalesCents: API.models.USDCents;
    toGoSalesCents: API.models.USDCents;
    driveThruSalesCents: API.models.USDCents;
    deliverySalesCents: API.models.USDCents;
    startTime?: string | null;
    endTime?: string | null;
};

export type BalanceReport = {
    summary: BalanceSummary;
    orderModes: OrderModeSummary[];
    familyGroups: FamilyGroupSummary[];
    discounts: DiscountSummary[];
    paymentTypes: PaymentTypeSummary[];
    cashSummaries: CashSummary[];
};

export type BalanceReportAPIResponse = Omit<BalanceReport, "cashSummaries"> & {
    cashSummaries: CashSummaryAPIResponse[];
};

export type BalanceSummary = {
    netSalesCents: API.models.USDCents;
    serviceChargesCents: API.models.USDCents;
    taxCollectedCents: API.models.USDCents;
    totalDiscountsCents: API.models.USDCents;
    managerVoids: {
        numApplied: number;
        sumCents: API.models.USDCents;
    };
    cancels: {
        numApplied: number;
        sumCents: API.models.USDCents;
    };
};

export type OrderModeSummary = {
    mode: "delivery" | "in_store" | "carry_out" | "drive_thru";
    numChecks: number;
    checkAverageCents: API.models.USDCents;
    checkTotalCents: API.models.USDCents;
    averageOrderTimeMs?: number | null;
};

export type FamilyGroupSummary = {
    name: string;
    subtotalCents: API.models.USDCents;
    amount: number;
};

export type DiscountSummary = {
    name: string;
    amountCents: API.models.USDCents;
};

export type PaymentTypeSummary = {
    type: PaymentType;
    tenderName?: string | null;
    count: number;
    totalChargedCents: API.models.USDCents;
};

export type CashSummary = {
    eventName: string;
    createDate: Date;
    totalAmountCents: API.models.USDCents;
    overAmountCents: API.models.USDCents;
    reason?: string | null;
};

export type CashSummaryAPIResponse = Omit<CashSummary, "createDate"> & {
    createDate: string;
};

export type SalesReport = {
    categories: CategorySales[];
    totals: SalesDetailSummary;
};

export type CategorySales = {
    name: string;
    itemsSold: number;
    salesPercentage: number;
    totalCents: API.models.USDCents;
    entities: CategorySalesEntity[];
};

export type CategorySalesEntity = {
    plu: number;
    name: string;
    quantity: number;
    percentage: number;
    voidQuantity: number;
    voidPercentage: number;
    netSales: number;
    netSalesCents: API.models.USDCents;
    netSalesPercentage: number;
};

export type SalesDetailSummary = {
    salesQuantity: number;
    salesSubtotalCents: API.models.USDCents;
    discountQuantity: number;
    discountSubtotalCents: API.models.USDCents;
    voidQuantity: number;
    voidSubtotalCents: API.models.USDCents;
    subtotalSumCents: API.models.USDCents;
};

export type ProcessPaymentResponse = {
    isApproved: boolean;
    ccType: VonageCCType;
    cardNumber: string;
    cardholderName: string;
    authId: string;
    termId: string;
    merchId: string;
    detailedStatusCode: string;
    statusCode: string;
    status: string;
    statusDescription: string;
    transactionAmount: API.models.USDCents;
    approvedAmount: API.models.USDCents;
    tipAmount: API.models.USDCents;
    currentBalance: API.models.USDCents;
    transactionId: string;
    transactionShortId: string;
    cardBrandType: string;
    cardExpMonth: string;
    cardExpYear: string;
    cardToken: string;
    ctroutd: string;
    troutd: string;
};

export type CurrentOrderPayment = {
    cashPaidCents: API.models.USDCents;
    changeOwedCents: API.models.USDCents;
    giftCardsApplied: AppliedGiftCard[];
    compCardsApplied: AppliedCompCard[];
    creditCardsApplied: AppliedCreditCard[];
    ezCaterApplied: boolean;
    externalTendersApplied: AppliedExternalTender[];
};

export type TillAssignments = {
    numTills: number;
    assignedTills: {
        tillNumber: number;
        deviceId: string | null;
    }[];
};

export type LaneAssignment = "lane_1" | "lane_2" | "both";

export type HealthCheckStatus = {
    ms: number;
    error: string | null;
};

export type ExhaustiveHealthCheckResponse = {
    accountImport: HealthCheckStatus;
    cardReaders: {
        name: string;
        status: HealthCheckStatus;
    }[];
    cloudwatch: {
        name: string;
        status: HealthCheckStatus;
    }[];
    cognito: HealthCheckStatus;
    mobileApi: HealthCheckStatus;
    printers: {
        name: string;
        status: HealthCheckStatus;
    }[];
    punchh: HealthCheckStatus;
    qsr: {
        name: string;
        status: HealthCheckStatus;
    }[];
    sentry: HealthCheckStatus;
    sso: HealthCheckStatus;
    tracks: HealthCheckStatus;
    tracksDiscount: HealthCheckStatus;
    tracksIdentity: HealthCheckStatus;
    tracksPurchase: HealthCheckStatus;
    tracksMenu: HealthCheckStatus;
};

export type TillCurrentAmountResponse = {
    currentAmountCents: API.models.USDCents;
    suggestedPullAmountCents: API.models.USDCents | null;
    maxPullAmountCents: API.models.USDCents | null;
};

export type TillCashPullRequest = {
    tillNumber: number;
    pulledAmountCents: number;
};

// To update paid in reasons, just update array below
export const PAID_IN_REASONS = [
    "change_replenishment",
    "purchase_refund",
    "petty_cash_return",
] as const;
export type PaidInReason = typeof PAID_IN_REASONS[number];
// To update paid out reasons, just update array below
export const PAID_OUT_REASONS = [
    "vendor_payment",
    "employee_reimbursement",
    "office_supplies",
    "food_purchase",
    "beverage_purchase",
    "paper_purchase",
    "repair",
] as const;
export type PaidOutReason = typeof PAID_OUT_REASONS[number];

export type QsrSelection = "ketchup" | "utensils" | "napkins";

export type KioskDeviceName = "kiosk1" | "kiosk2" | "kiosk3" | "kiosk4";
